<template>
  <v-expansion-panels elevation="0">
    <v-expansion-panel>
      <v-expansion-panel-title>{{ label }}</v-expansion-panel-title>
      <v-expansion-panel-text>
        <v-row>
          <v-col
            v-for="field in fields"
            :key="field.value"
            :cols="`${field.col || 12}`"
          >
            <component
              v-if="!field.children"
              :is="field.component"
              :model-value="modelValue?.[field.value] ?? null"
              @update:model-value="val =>
                emit('update:modelValue', {
                  ...modelValue,
                  [field.value]: val
                })"
              v-bind="getFieldProps(field)"
              :rules="field.required ? [v => !!v || 'Campo obligatorio'] : []"
            />

            <component
              v-else
              :is="field.component"
              v-model="modelValue[field.value]"
              :fields="field.children"
              :summary="field.summary"
              :label="field.label"
            />
          </v-col>
        </v-row>
      </v-expansion-panel-text>
    </v-expansion-panel>
  </v-expansion-panels>
</template>

<script setup lang="ts">
import { onMounted, watch } from 'vue';
import type { PropType } from 'vue';
import type { FormField } from '@/types/form';
import { remoteOptionsRegistry } from '@/lib/remoteOptions';

const props = defineProps({
  fields: Array as PropType<FormField[]>,
  modelValue: Object as PropType<Record<string, any>>,
  label: { type: String, default: 'Grupo de Campos' },
});

const emit = defineEmits(['update:modelValue']);

// Agrega mapeo inicial de valores planos a objetos
onMounted(async () => {
  for (const field of props.fields || []) {
    if (field.fetchOptions && remoteOptionsRegistry[field.fetchOptions]) {
      const fetchFn = remoteOptionsRegistry[field.fetchOptions];
      const options = await fetchFn('');
      field.options = options;

      const val = props.modelValue?.[field.value];
      const itemValueKey = field.itemValue || 'value';

      if (val && typeof val !== 'object') {
        const match = options.find(opt => opt[itemValueKey] === val);
        if (match) {
          emit('update:modelValue', {
            ...props.modelValue,
            [field.value]: match,
          });
        }
      }
    }
  }
});

const getFieldProps = (field: FormField) => {
  const props: Record<string, any> = {
    label: field.label,
    placeholder: field.placeholder ?? '',
    clearable: true,
    density: 'compact',
    type: field.type || 'text',
    prefix: field.prefix,
    suffix: field.suffix,
    'prepend-inner-icon': field.icon,
    disabled: field.permission === 'disabled',
    readonly: field.readonly,
    'item-title': field.itemText || 'label',
    'item-value': field.itemValue || 'value',
  };

  if (field.options) {
    props.items = field.options;
  }

  if (field.fetchOptions && remoteOptionsRegistry[field.fetchOptions]) {
    props['onUpdate:search'] = async (val: string) => {
      if (!val || val.length < 2) return;
      const fetchFn = remoteOptionsRegistry[field.fetchOptions!];
      const results = await fetchFn(val);
      field.options = results;
      props.items = results;
    };
  }

  return props;
};
</script>
